import { ActionsHook } from "@/lib/Components/Actions/ActionGroupButtons";
import { useMatchRoute, useNavigate } from "@tanstack/react-router";
import { BellIcon, LinkIcon } from "lucide-react";
import {
  ArchiveBoxIcon,
  CalendarDaysIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { OfficeUpdateDialog } from "@/app/Offices/Components/OfficeUpdateDialog";
import { ConfirmDeleteDialog } from "@/lib/Components/Dialog/ConfirmDeleteDialog";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import {
  archiveSupplierOfficeMutation,
  deleteSupplierOfficeMutation,
} from "@/app/Offices/GraphQL/supplierOfficeMutations";
import { SupplierOfficeListItem } from "@/app/Offices/GraphQL/supplierOfficeListQuery";
import { OfficeNotificationDialog } from "@/app/Offices/Components/Dialogs/OfficeNotificationDialog";
import { useTranslations } from "use-intl";

export function useOfficeActions(): ActionsHook<SupplierOfficeListItem> {
  const t = useTranslations("office.actions");
  const { open } = useDialog(OfficeUpdateDialog);
  const { open: openDeleteDialog } = useDialog(ConfirmDeleteDialog);
  const { mutateAsync } = useGqlMutation(deleteSupplierOfficeMutation);
  const { mutateAsync: archiveOffice } = useGqlMutation(
    archiveSupplierOfficeMutation,
  );
  const { open: openNotifications } = useDialog(OfficeNotificationDialog);

  const navigate = useNavigate();
  const matchRoute = useMatchRoute();
  const params = matchRoute({
    to: "/$tenant/$orgId/supplier/{-$supplierId}/offices/$id",
  });

  return (model) => {
    return [
      {
        label: t("actions_menu"),
        items: [
          {
            actions: [
              {
                isHiddenTopBar: true,
                isHidden: params !== false,
                label: t("edit"),
                Icon: PencilIcon,
                handleAction: async () => {
                  open({
                    officeId: model.id,
                  });
                },
              },
              {
                isHiddenTopBar: true,
                isHidden: params !== false,
                label: t("change_open_hours"),
                Icon: CalendarDaysIcon,
                handleAction: async () => {
                  await navigate({
                    from: "/$tenant/$orgId/supplier/{-$supplierId}",
                    to: "/$tenant/$orgId/supplier/{-$supplierId}/offices/$id/open-hours",
                    params: (prev) => ({
                      ...prev,
                      id: model.id,
                    }),
                  });
                },
              },
              {
                isHiddenTopBar: true,
                isHidden: params !== false,
                label: t("add_notification"),
                Icon: BellIcon,
                handleAction: () => {
                  openNotifications({
                    officeId: model.id,
                  });
                },
              },
            ],
          },
          {
            actions: [
              {
                isHiddenTopBar: true,
                isHidden: params !== false,
                label: t("view_office"),
                Icon: LinkIcon,
                handleAction: async () => {
                  await navigate({
                    from: "/$tenant/$orgId/supplier/{-$supplierId}",
                    to: "/$tenant/$orgId/supplier/{-$supplierId}/offices/$id",
                    params: (prev) => ({
                      ...prev,
                      id: model.id,
                    }),
                  });
                },
              },
            ],
          },
          {
            actions: [
              {
                label: t("archive"),
                Icon: ArchiveBoxIcon,
                isHiddenTopBar: true,

                color: "purple",
                handleAction: () => {
                  openDeleteDialog({
                    message: t("archive_confirm_message"),
                    buttonText: t("archive_button", { name: model.name }),
                    title: t("archive_title", { name: model.name }),
                    onDelete: async () => {
                      await archiveOffice({ id: model.id });
                    },
                  });
                },
              },
              {
                label: t("delete"),
                Icon: TrashIcon,
                color: "red",
                isHidden: !model.archived_at,
                isHiddenTopBar: true,
                handleAction: () => {
                  openDeleteDialog({
                    message: t("delete_confirm_message"),
                    buttonText: t("delete_button", { name: model.name }),
                    title: t("delete_title", { name: model.name }),
                    onDelete: async () => {
                      await mutateAsync({ id: model.id });
                      await navigate({
                        from: "/$tenant/$orgId/supplier/{-$supplierId}",
                        to: "/$tenant/$orgId/supplier/{-$supplierId}/offices",
                        params: (prev) => prev,
                      });
                    },
                  });
                },
              },
            ],
          },
        ],
      },
    ];
  };
}
